import { call, put, select, takeEvery } from '@redux-saga/core/effects'
import { Client } from 'api/client'
import axios, { AxiosResponse } from 'axios'
import { LOCAL_STORAGE_CASHFLOW } from 'shared/constants/localStorageVariables'
import {
  getKeyFigureAccountsById,
  getKeyFigureLoansById,
  getKeyFigureUnpaidInvoicesById,
} from '../../api/keyFigures.api'
import {
  IKeyFiguresAccounts,
  IKeyFiguresLoan,
  IKeyFiguresLiquidity,
  IKeyFiguresUnpaidInvoices,
} from '../../models/KeyFigures'
import {
  getKeyFigureCashflow,
  getKeyFigureJointCostsById,
  getKeyFigureLiquidity,
  getKeyFigureOperatingCostsById,
  getKeyFigureReportsById,
  getKeyFigureResultsById,
  getShouldShowCashflow,
} from './../../api/keyFigures.api'
import {
  IKeyFiguresCashflow,
  IKeyFiguresJointCost,
  IKeyFiguresReports,
  IKeyFiguresResultsModel,
  IOperatingCostKeyFiguresModel,
} from './../../models/KeyFigures'
import {
  KEY_FIGURES_LOANS_SUCCESS,
  KEY_FIGURES_ACCOUNTS_FAILED,
  KEY_FIGURES_ACCOUNTS_SUCCESS,
  KEY_FIGURES_LOANS_FAILED,
  KEY_FIGURES_JOINTCOSTS_FAILED,
  KEY_FIGURES_JOINTCOSTS_SUCCESS,
  KEY_FIGURES_LIQUIDITY_FAILED,
  KEY_FIGURES_LIQUIDITY_REQUESTED,
  KEY_FIGURES_LIQUIDITY_SUCCESS,
  KEY_FIGURES_OPERATINGCOSTS_FAILED,
  KEY_FIGURES_OPERATINGCOSTS_REQUESTED,
  KEY_FIGURES_OPERATINGCOSTS_SUCCESS,
  KEY_FIGURES_UNPAIDINVOICES_FAILED,
  KEY_FIGURES_UNPAIDINVOICES_SUCCESS,
  KEY_FIGURES_RESULTS_SUCCESS,
  KEY_FIGURES_RESULTS_FAILED,
  KEY_FIGURES_RESULTS_REQUESTED,
  KEY_FIGURES_SET_ACTIVECHART_REQUESTED,
  KEY_FIGURES_SET_ACTIVECHART_SUCCESS,
  KEY_FIGURES_FETCH_CASHFLOW_REQUESTED,
  KEY_FIGURES_FETCH_CASHFLOW_SUCCESS,
  KEY_FIGURES_FETCH_CASHFLOW_FAILED,
  KEY_FIGURES_CASHFLOW_INIT,
  KEY_FIGURES_SET_CASHFLOW_ENABLED,
  KEY_FIGURES_REPORTS_SUCCESS,
  KEY_FIGURES_REPORTS_FAILED,
  KEY_FIGURES_REPORTS_REQUESTED,
} from './actionTypes'
import {
  KEY_FIGURES_ACCOUNTS_REQUESTED,
  KEY_FIGURES_JOINTCOSTS_REQUESTED,
  KEY_FIGURES_LOANS_REQUESTED,
  KEY_FIGURES_UNPAIDINVOICES_REQUESTED,
} from './actionTypes'
import { KeyFiguresActionTypes } from './types'
import { getActiveClient } from '../clients/selectors'

const getLocalStorageKey = (clientId: string) =>
  `${LOCAL_STORAGE_CASHFLOW}-${clientId}`

function* fetchKeyFiguresAccounts(action: KeyFiguresActionTypes) {
  try {
    const clientId = action.payload.id
    const response: AxiosResponse<IKeyFiguresAccounts[]> = yield call(
      getKeyFigureAccountsById,
      clientId,
      action.payload?.cancelToken
    )
    const accounts: IKeyFiguresAccounts[] = response.data

    yield put({
      type: KEY_FIGURES_ACCOUNTS_SUCCESS,
      payload: accounts,
    })
  } catch (e) {
    if (axios.isCancel(e)) return
    yield put({
      type: KEY_FIGURES_ACCOUNTS_FAILED,
      payload: e,
    })
  }
}

function* fetchKeyFiguresOperatingCosts(action: KeyFiguresActionTypes) {
  try {
    const clientId = action.payload.id
    const year = action.payload.year
    const response: AxiosResponse<IOperatingCostKeyFiguresModel[]> = yield call(
      getKeyFigureOperatingCostsById,
      clientId,
      year
    )
    const opreatingcosts: IOperatingCostKeyFiguresModel[] = response.data

    yield put({
      type: KEY_FIGURES_OPERATINGCOSTS_SUCCESS,
      payload: opreatingcosts,
    })
  } catch (e) {
    yield put({
      type: KEY_FIGURES_OPERATINGCOSTS_FAILED,
      payload: e,
    })
  }
}

function* fetchKeyFiguresLoans(action: KeyFiguresActionTypes) {
  try {
    const clientId = action.payload.id
    const skipcache = action.payload.skipcache
    const response: AxiosResponse<IKeyFiguresLoan[]> = yield call(
      getKeyFigureLoansById,
      clientId,
      skipcache
    )
    const loans: IKeyFiguresLoan[] = response.data
    yield put({
      type: KEY_FIGURES_LOANS_SUCCESS,
      payload: loans,
    })
  } catch (e) {
    yield put({
      type: KEY_FIGURES_LOANS_FAILED,
      payload: e,
    })
  }
}

function* fetchKeyFiguresReports(action: KeyFiguresActionTypes) {
  try {
    const client: Client = yield select(getActiveClient)
    const response: AxiosResponse<IKeyFiguresReports[]> = yield call(
      getKeyFigureReportsById,
      client.id
    )
    const reports: IKeyFiguresReports[] = response.data
    yield put({
      type: KEY_FIGURES_REPORTS_SUCCESS,
      payload: reports,
    })
  } catch (e) {
    yield put({
      type: KEY_FIGURES_REPORTS_FAILED,
      payload: e,
    })
  }
}

function* fetchKeyFiguresJointCosts(action: KeyFiguresActionTypes) {
  try {
    const clientId = action.payload.id
    const response: AxiosResponse<IKeyFiguresJointCost[]> = yield call(
      getKeyFigureJointCostsById,
      clientId
    )
    const jointcosts: IKeyFiguresJointCost[] = response.data
    yield put({
      type: KEY_FIGURES_JOINTCOSTS_SUCCESS,
      payload: jointcosts,
    })
  } catch (e) {
    yield put({
      type: KEY_FIGURES_JOINTCOSTS_FAILED,
      payload: e,
    })
  }
}

function* fetchKeyFigureLiquidity(action: KeyFiguresActionTypes) {
  try {
    const clientId = action.payload.id
    const periode = action.payload.periode
    const response: AxiosResponse<IKeyFiguresLiquidity> = yield call(
      getKeyFigureLiquidity,
      clientId,
      periode
    )
    const liquidity: IKeyFiguresLiquidity = response.data
    yield put({
      type: KEY_FIGURES_LIQUIDITY_SUCCESS,
      payload: liquidity,
    })
  } catch (e) {
    yield put({
      type: KEY_FIGURES_LIQUIDITY_FAILED,
      payload: e,
    })
  }
}

function* fetchKeyFiguresUnpaidInvoices(action: KeyFiguresActionTypes) {
  try {
    const clientId = action.payload.id

    const response: AxiosResponse<IKeyFiguresUnpaidInvoices[]> = yield call(
      getKeyFigureUnpaidInvoicesById,
      clientId
    )
    const unpaidInvoices: IKeyFiguresUnpaidInvoices[] = response.data
    let key: number = 0
    unpaidInvoices.forEach((invoice) => {
      invoice.key = key++
      return invoice
    })
    yield put({
      type: KEY_FIGURES_UNPAIDINVOICES_SUCCESS,
      payload: unpaidInvoices,
    })
  } catch (e) {
    yield put({
      type: KEY_FIGURES_UNPAIDINVOICES_FAILED,
      payload: e,
    })
  }
}

function* fetchKeyFiguresResults(action: KeyFiguresActionTypes) {
  try {
    const clientId = action.payload.id
    const year = action.payload.year

    const response: AxiosResponse<IKeyFiguresResultsModel[]> = yield call(
      getKeyFigureResultsById,
      clientId,
      year
    )
    const results: IKeyFiguresResultsModel[] = response.data
    yield put({
      type: KEY_FIGURES_RESULTS_SUCCESS,
      payload: results,
    })
  } catch (e) {
    yield put({
      type: KEY_FIGURES_RESULTS_FAILED,
      payload: e,
    })
  }
}

function* setKeyFiguresActiveChart(action: KeyFiguresActionTypes) {
  yield put({
    type: KEY_FIGURES_SET_ACTIVECHART_SUCCESS,
    payload: action.payload,
  })
}

function* initCashflow() {
  try {
    const { id }: { id: string } = yield select(getActiveClient)

    const response: AxiosResponse<boolean> = yield call(
      getShouldShowCashflow,
      id
    )
    yield put({
      type: KEY_FIGURES_SET_CASHFLOW_ENABLED,
      payload: response.data,
    })

    const fromLocalStorage = localStorage.getItem(getLocalStorageKey(id))

    if (fromLocalStorage) {
      yield put({
        type: KEY_FIGURES_FETCH_CASHFLOW_SUCCESS,
        payload: JSON.parse(fromLocalStorage),
      })
    }
  } catch (e) {
    console.log('Det skjedde en feil ved uthenting av likviditet settings', e)
  }
}

function* fetchCashflow() {
  try {
    const { id }: { id: string } = yield select(getActiveClient)

    const response: AxiosResponse<IKeyFiguresCashflow> = yield call(
      getKeyFigureCashflow,
      id
    )

    const mapped = { ...response.data, lastUpdated: new Date() }

    // Store in localstorage for user and client, due to heavy request
    localStorage.setItem(getLocalStorageKey(id), JSON.stringify(mapped))

    yield put({
      type: KEY_FIGURES_FETCH_CASHFLOW_SUCCESS,
      payload: mapped,
    })
  } catch (e) {
    yield put({
      type: KEY_FIGURES_FETCH_CASHFLOW_FAILED,
      payload: 'Det skjedde en feil ved uthenting av likviditet',
    })
  }
}

function* keyFiguresSaga() {
  yield takeEvery(KEY_FIGURES_LOANS_REQUESTED, fetchKeyFiguresLoans)
  yield takeEvery(KEY_FIGURES_REPORTS_REQUESTED, fetchKeyFiguresReports)
  yield takeEvery(KEY_FIGURES_ACCOUNTS_REQUESTED, fetchKeyFiguresAccounts)
  yield takeEvery(KEY_FIGURES_JOINTCOSTS_REQUESTED, fetchKeyFiguresJointCosts)
  yield takeEvery(KEY_FIGURES_LIQUIDITY_REQUESTED, fetchKeyFigureLiquidity)
  yield takeEvery(
    KEY_FIGURES_UNPAIDINVOICES_REQUESTED,
    fetchKeyFiguresUnpaidInvoices
  )
  yield takeEvery(
    KEY_FIGURES_OPERATINGCOSTS_REQUESTED,
    fetchKeyFiguresOperatingCosts
  )
  yield takeEvery(KEY_FIGURES_RESULTS_REQUESTED, fetchKeyFiguresResults)
  yield takeEvery(
    KEY_FIGURES_SET_ACTIVECHART_REQUESTED,
    setKeyFiguresActiveChart
  )
  yield takeEvery(KEY_FIGURES_CASHFLOW_INIT, initCashflow)
  yield takeEvery(KEY_FIGURES_FETCH_CASHFLOW_REQUESTED, fetchCashflow)
}

export default keyFiguresSaga
